import type { ResourcePointer } from "./common";
import { InvalidTransitionError, NotFoundError, PreconditionFailedError } from "./errors";
import type { Brand } from "./lib/brand";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";
import type { VisitId } from "./visit.model";

/** What a caller needs to read a filed document's bytes, and no more of it. */
export interface ChartableDocumentContent {
  binaryId: BinaryId;
  contentType: string;
}

/** Stable PatientDocument identity, independent of the FHIR row locator. */
export type PatientDocumentId = DomainId<"PatientDocumentId">;

export function asPatientDocumentId(value: string): PatientDocumentId {
  if (!value) throw new PreconditionFailedError("A patient document identity cannot be empty");
  return value as PatientDocumentId;
}

export type PatientDocumentStatus = "current" | "entered-in-error";

const TRANSITIONS: Record<PatientDocumentStatus, PatientDocumentStatus[]> = {
  current: ["entered-in-error"],
  "entered-in-error": [],
};

/**
 * The purposes we file under.
 *
 * The codes live here because the caller choosing one is a domain decision —
 * each upload handler names its own. How they are REPRESENTED in FHIR (system,
 * display, which element they land in) stays in the codec.
 */
export const PATIENT_DOCUMENT_PURPOSES = {
  /** Existing material filed into the chart to be read: referrals, prior records. */
  patientRecord: "patient-record",
  /** The patient's signed copy of a consent form: the record of what they agreed to. */
  signedConsent: "signed-consent",
  /** Produced by observing or performing. The document IS the finding. */
  observationCapture: "observation-capture",
} as const;

/** Purposes whose documents stay filed: the record itself, not a copy of something else. */
const NON_RETRACTABLE_PURPOSES = new Set<string>([PATIENT_DOCUMENT_PURPOSES.signedConsent]);

const NON_RETRACTABLE_REFUSAL = "A signed consent is the record of what the patient agreed to and cannot be retracted";

/**
 * Full creation context: what the upload was for, and the document itself.
 *
 * The classification sits BESIDE the content rather than inside it because it
 * is not a fact about the file — the same bytes filed as reference material and
 * captured as a finding are different documents to a clinician, and only the
 * caller filing it knows which.
 *
 * It classifies PURPOSE, never who uploaded. `uploadedBy` already answers who,
 * and a patient and a practitioner filing prior records are doing the same
 * thing; a capture taken while observing is not.
 *
 * Open string. How a classification is REPRESENTED lives in the codec, not here.
 */
export type PatientDocumentType = {
  classification?: string;
  content: PatientDocumentContent;
};

/** The document itself: the file, who filed it, and what it hangs off. */
export interface PatientDocumentContent {
  patientId: PatientId;
  visitId?: VisitId;
  /** The id of the stored Binary, never a URL — a URL expires. */
  binaryId: BinaryId;
  contentType: string;
  title: string;
  /** Exact original basename, retained independently of the display title. */
  sourceFileName?: string;
  size?: number;
  uploadedBy: ResourcePointer;
  uploadedAt: string;
  /**
   * What a second upload of the same file at the same visit matches, from
   * `captureKeyFor`. Absent on anything but a capture, and released when the
   * document is retracted.
   */
  captureKey?: string;
}

/** What makes two uploads one capture: the same bytes, patient and visit. */
export interface CaptureContentScope {
  patientId: PatientId;
  visitId: VisitId;
  /** A digest of the bytes. How it is computed is the caller's business. */
  contentDigest: string;
}

/**
 * Separates the parts of a capture key, and may appear in none of them — a
 * component carrying one could otherwise spell a key belonging to another
 * patient or visit.
 */
const KEY_SEPARATOR = ":";

export type PatientDocumentProps = PatientDocumentContent &
  Omit<PatientDocumentType, "content"> & { id: PatientDocumentId; status: PatientDocumentStatus };

/** A file filed against a patient. Identity here, content behind the binary store. */
export class PatientDocument {
  private state: PatientDocumentProps;

  private constructor(props: PatientDocumentProps) {
    if (!props.patientId) throw new PreconditionFailedError("A patient document requires a patient");
    if (!props.binaryId) throw new PreconditionFailedError("A patient document requires stored content");
    if (!props.contentType) throw new PreconditionFailedError("A patient document requires a content type");
    if (!props.title) throw new PreconditionFailedError("A patient document requires a title");
    if (!props.uploadedBy?.id) throw new PreconditionFailedError("A patient document requires an uploader");
    if (!Object.hasOwn(TRANSITIONS, props.status)) throw new PreconditionFailedError("Unknown patient document status");
    if (props.captureKey !== undefined) {
      // A key is only ever this document's own scope, so one built elsewhere
      // cannot be filed here — that would reuse this document for content it
      // does not hold.
      const scope = `${props.patientId}${KEY_SEPARATOR}${props.visitId ?? ""}${KEY_SEPARATOR}`;
      if (!props.captureKey.startsWith(scope) || props.captureKey.length === scope.length) {
        throw new PreconditionFailedError("A capture key must be this document's own patient, visit and content");
      }
    }
    this.state = structuredClone(props);
  }

  static create(type: PatientDocumentType & { id: PatientDocumentId }): PatientDocument {
    return new PatientDocument({
      ...structuredClone(type.content),
      classification: type.classification,
      id: asPatientDocumentId(type.id),
      status: "current",
    });
  }

  static restore(snapshot: PatientDocumentProps): PatientDocument {
    return new PatientDocument(snapshot);
  }

  /**
   * The key a capture of these bytes is filed under here.
   *
   * The scoping is the rule: within one visit the same file is plainly the
   * same clinical act, so a second upload of it must reuse the document
   * already filed rather than leave a copy in a chart nothing can delete.
   * Across visits it is not — reusing would attach a capture to an encounter
   * it was not taken during, which is clinically wrong.
   *
   * The key is a scoped value rather than a digest alone so that finding one
   * never crosses a chart: a search for it can only ever match this patient's
   * documents, and there is nothing to filter down afterwards.
   */
  static captureKeyFor(scope: CaptureContentScope): string {
    for (const part of [scope.patientId, scope.visitId, scope.contentDigest]) {
      if (!part) throw new PreconditionFailedError("A capture key needs a patient, a visit and a content digest");
      if (part.includes(KEY_SEPARATOR)) {
        throw new PreconditionFailedError(`A capture key component cannot contain '${KEY_SEPARATOR}'`);
      }
    }
    return [scope.patientId, scope.visitId, scope.contentDigest].join(KEY_SEPARATOR);
  }

  // ── facts ──

  get id(): PatientDocumentId {
    return this.state.id;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  /** Filed in error. Not a clinical state — the document should not be listed. */
  get isRetracted(): boolean {
    return this.state.status === "entered-in-error";
  }

  /** Still filed, and filed under a purpose that lets it be retracted. */
  get isRetractable(): boolean {
    return !this.isRetracted && !this.isKeptByPurpose;
  }

  private get isKeptByPurpose(): boolean {
    return this.state.classification !== undefined && NON_RETRACTABLE_PURPOSES.has(this.state.classification);
  }

  snapshot(): PatientDocumentProps {
    return structuredClone(this.state);
  }

  // ── operations ──

  /**
   * The stored content to chart a reading from, on this patient's chart.
   *
   * A behaviour, not a permission slip: the caller asks for the content and
   * gets it or an error, the same way `recordObservation` enforces its own
   * preconditions inside itself rather than publishing them. Nothing here
   * hands a predicate back to branch on, and the two rules are the business's,
   * so they live where every caller reaches them — a bot and a saga included.
   *
   * It returns the content alone rather than the document, because "give me
   * this document's content to chart from" is a sentence and "give me this
   * document, having checked some things" is not.
   *
   * The cross-chart case is NOT FOUND rather than forbidden. The question is
   * "this patient's document with this id", and a document on another chart is
   * not one; a forbidden would confirm it exists, which is the one thing a
   * caller guessing identities wants to learn. A retracted document IS named
   * out loud — that caller already knows it is there.
   */
  contentToChartFrom(patientId: PatientId): ChartableDocumentContent {
    this.assertChartableOn(patientId);
    return { binaryId: this.state.binaryId, contentType: this.state.contentType };
  }

  /**
   * This document's identity, to store on a reading that was read off it.
   *
   * The same sentence as `contentToChartFrom` for the caller that already has
   * the numbers: a reading may cite a document exactly when a reading could
   * have been charted off it. It exists so that caller ASKS the rule rather
   * than restating it — it wants no bytes, and a method that returned content
   * it threw away would invite somebody to skip the call entirely.
   */
  citedAsSourceFor(patientId: PatientId): PatientDocumentId {
    this.assertChartableOn(patientId);
    return this.state.id;
  }

  /**
   * This document's identity, to file a capture of that content against
   * instead of a second copy of it.
   *
   * The same shape as `citedAsSourceFor`: the caller asks for the identity and
   * gets it or an error, rather than being handed a predicate to branch on.
   * The rule it enforces is that a document is only ever reused for the
   * content it actually holds — a key from another patient, another visit or
   * other bytes is not this document's.
   *
   * The retraction refusal is stated HERE rather than left to `retract()`
   * clearing the key and the search therefore missing. That is one mechanism,
   * and it is the wrong one to rest a chart-safety invariant on: an identifier
   * surviving a retraction anywhere would decode straight back into state and
   * a retracted document would be reused as a live capture. The two sibling
   * behaviours refuse a retracted document, and so does this one.
   */
  reusedForCaptureOf(key: string): PatientDocumentId {
    this.assertNotRetracted();
    if (!this.state.captureKey || this.state.captureKey !== key) {
      throw new PreconditionFailedError("This document is not the filed capture of that content");
    }
    return this.state.id;
  }

  /** The rule both charting sentences enforce, stated once and never published. */
  private assertChartableOn(patientId: PatientId): void {
    if (this.state.patientId !== patientId) throw new NotFoundError("PatientDocument", this.state.id);
    this.assertNotRetracted();
  }

  /**
   * Said out loud wherever it is raised, unlike the cross-chart refusal: these
   * callers already know the document is there, and a bare not-found would
   * read as a bug rather than as a retracted filing.
   */
  private assertNotRetracted(): void {
    if (this.isRetracted) {
      throw new PreconditionFailedError("This document has been retracted and cannot be charted from");
    }
  }

  /**
   * It should never have been filed. The one change a filed document's status
   * can undergo, and only where its purpose allows it.
   */
  retract(): void {
    if (this.isKeptByPurpose) throw new PreconditionFailedError(NON_RETRACTABLE_REFUSAL);
    if (!TRANSITIONS[this.state.status]?.includes("entered-in-error")) {
      throw new InvalidTransitionError(this.state.status, `PatientDocument:${this.state.status}→entered-in-error`);
    }
    this.state.status = "entered-in-error";
    // Retracting says the filing should never have happened, so the file comes
    // free with it: the same bytes arriving afterwards are a fresh act, and a
    // retracted document still holding the key would make re-uploading the
    // file impossible.
    this.state.captureKey = undefined;
  }
}

/** Opaque persisted Binary locator; not a domain identity. */
export type BinaryId = Brand<string, "BinaryId">;
export function asBinaryId(value: string): BinaryId {
  return value as BinaryId;
}
